import React, { useEffect, useRef, useState } from 'react';
import { Col, Empty, Form, Row, Spin, UploadFile } from 'antd';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { clearFieldErrors, handleFormErrors } from '../../common/errorHelpers';
import { getFormFieldData } from '../../common/filterHelpers';
import { SearchFormFieldEnum } from '../../common/searchFormField.types';
import { useGetProject } from '../../common/useGetProject';
import { BreadcrumbItem, Breadcrumbs } from '../../components/Breadcrumbs';
import { PageRoutesEnum } from '../../utils/routes';
import { useOpenMatchingPage } from '../candidate-project-matching/useOpenMatchingPage';

import {
  mapErrorFieldsToFormLabels,
  mapPreferredStartDateToRequest,
  mapProjectDataToFormValues,
  normalizeOptionalText,
  ProjectDataForm,
} from './ProjectDataForm';
import { ProjectDetailsPageHeader } from './ProjectDetailsPageHeader';
import { ProjectRecruitmentsTable } from './ProjectRecruitmentsTable';
import { ProjectRemindersTable } from './ProjectRemindersTable';
import { SimilarProjectsModal } from './SimilarProjectsModal';
import { useUpdateProject } from './useUpdateProject';

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

export const mapOptionalNumberToUpdateRequest = (value: number | undefined, isTouched: boolean) => {
  if (!isTouched) return undefined;

  return value === undefined ? null : value;
};

export const mapOptionalStringToUpdateRequest = (value: string | undefined, isTouched: boolean) => {
  if (!isTouched) return undefined;

  return normalizeOptionalText(value) ?? null;
};

const hourlyRateFieldNames = [
  'clientHourlyRateMin',
  'clientHourlyRateMax',
  'candidateHourlyRateMin',
  'candidateHourlyRateMax',
] as const;

type HourlyRateFieldName = (typeof hourlyRateFieldNames)[number];

type HourlyRatesUpdateRequest = Partial<Record<HourlyRateFieldName, number | null>>;

const mapHourlyRatesToUpdateRequest = (
  formFieldsValue: Record<string, unknown>,
  form: ReturnType<typeof Form.useForm>[0]
): HourlyRatesUpdateRequest =>
  hourlyRateFieldNames.reduce(
    (rates, fieldName) => ({
      ...rates,
      [fieldName]: mapOptionalNumberToUpdateRequest(
        formFieldsValue[fieldName] as number | undefined,
        form.isFieldTouched(fieldName)
      ),
    }),
    {} as HourlyRatesUpdateRequest
  );

export const EditProjectPage: React.FC = () => {
  const { id } = useParams();

  const { data, isLoading, error } = useGetProject(id || '');
  const [form] = Form.useForm();
  const matchingBaseline = useRef<Record<string, unknown>>({});
  const updateProject = useUpdateProject();
  const navigate = useNavigate();
  const openMatchingPage = useOpenMatchingPage({ type: 'project', uuid: id || '' });

  const [attachments, setAttachments] = useState<UploadFile[] | null>(null);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);

  useEffect(() => {
    if (!data) return;

    const remoteAttachmentIds = data.attachments.map(({ fileId }) => fileId);
    const localAttachmentIds = new Set(attachments?.map((attachment) => attachment.uid));

    const isSizeEqual = remoteAttachmentIds.length === localAttachmentIds.size;
    const isIdSetEqual = isSizeEqual && remoteAttachmentIds.every((uid) => localAttachmentIds.has(uid));

    if (!attachments || !isIdSetEqual) {
      setAttachments(
        data.attachments.map((attachment) => ({
          ...attachment,
          uid: attachment.fileId,
        }))
      );
    }
  }, [data, attachments]);

  useEffect(() => {
    if (!data) return;

    form.resetFields();
    form.setFieldsValue(mapProjectDataToFormValues(data));
    matchingBaseline.current = form.getFieldsValue(true);
  }, [data, form]);

  // Similar projects modal owns its own data fetching and rendering.

  if (isLoading) {
    return (
      <MainContainer>
        <Spin tip="Loading project..." />
      </MainContainer>
    );
  }

  if (error || !data) {
    return (
      <MainContainer>
        <Empty description="Unable to load project" />
      </MainContainer>
    );
  }

  if (!attachments) {
    return (
      <MainContainer>
        <Spin tip="Preparing project data..." />
      </MainContainer>
    );
  }

  const handleSave = async () => {
    let formFieldsValue;

    try {
      formFieldsValue = await form.validateFields();
    } catch {
      return;
    }

    const formValues = {
      ...formFieldsValue,
      uuid: id,
      createDate: dayjs(),
      externalId: formFieldsValue.externalId ?? null,
      preferredStartDate: mapPreferredStartDateToRequest(formFieldsValue.preferredStartDate),
      ...mapHourlyRatesToUpdateRequest(formFieldsValue, form),
      hourlyRateNote: mapOptionalStringToUpdateRequest(
        formFieldsValue.hourlyRateNote,
        form.isFieldTouched('hourlyRateNote')
      ),
      contractTypes:
        getFormFieldData(SearchFormFieldEnum.ContractTypes).toPayloadValueFormatter?.(
          formFieldsValue[SearchFormFieldEnum.ContractTypes]
        ) ?? [],
    };

    const formData = new FormData();

    formData.append(
      'request',
      new Blob([JSON.stringify(formValues)], {
        type: 'application/json',
      })
    );

    updateProject.mutate(
      { queryParams: { id }, data: formData },
      {
        onSuccess: () => clearFieldErrors(form),
        onError: (error) => {
          handleFormErrors({ error, form, mapErrorFieldsToFormLabels });
        },
      }
    );
  };

  const syncAttachments = (localAttachments: UploadFile[]) => {
    const remoteAttachmentIds = data.attachments.map(({ fileId }) => fileId);
    const localAttachmentIds = new Set(localAttachments.map((attachment) => attachment.uid));
    const attachmentIdsToDelete = remoteAttachmentIds.filter(
      (remoteAttachmentId) => !localAttachmentIds.has(remoteAttachmentId)
    );

    const formData = new FormData();

    formData.append(
      'request',
      new Blob([JSON.stringify({ toDeleteAttachments: attachmentIdsToDelete })], {
        type: 'application/json',
      })
    );

    for (const attachment of localAttachments) {
      if (attachment.originFileObj instanceof Blob) {
        formData.append('newAttachments', attachment.originFileObj);
      }
    }

    updateProject.mutate(
      { queryParams: { id }, data: formData },
      {
        onError: (error) => {
          handleFormErrors({ error, form, mapErrorFieldsToFormLabels });
        },
      }
    );
  };

  const handleCancel = () => navigate(-1);

  const handleShowSimilarProjects = () => {
    setIsSimilarModalOpen(true);
  };

  const handleCloseSimilarProjects = () => {
    setIsSimilarModalOpen(false);
  };

  const breadcrumbItems = [
    {
      title: <BreadcrumbItem path={PageRoutesEnum.projects} title="Projects" />,
    },
    {
      title: <BreadcrumbItem title={`${data.name}`} />,
    },
  ];

  return (
    <MainContainer>
      <ProjectDetailsPageHeader
        onShowRecommendations={() => openMatchingPage(!isEqual(form.getFieldsValue(true), matchingBaseline.current))}
        title={<Breadcrumbs breadcrumbItems={breadcrumbItems} />}
        onSave={handleSave}
        onCancel={handleCancel}
        onShowSimilar={handleShowSimilarProjects}
      />
      <SimilarProjectsModal
        open={isSimilarModalOpen}
        onClose={handleCloseSimilarProjects}
        projectId={id}
        projectName={data.name}
      />
      <Row gutter={[15, 15]}>
        <Col xs={24}>
          <ProjectDataForm
            form={form}
            attachments={attachments}
            onAttachmentsChange={syncAttachments}
            initialData={data}
          />
        </Col>
        <Col xs={24}>
          <ProjectRecruitmentsTable projectData={data} />
        </Col>
        <Col xs={24}>
          <ProjectRemindersTable projectId={id ?? ''} />
        </Col>
      </Row>
    </MainContainer>
  );
};
